import React, { useState } from 'react';
import Nav from '../landing/nav';
import { FiInfo, FiUserCheck, FiEdit3, FiAlertCircle } from 'react-icons/fi';

const Guidelines = () => {
    const [menuOpen, setMenuOpen] = useState(false);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    return (
        <div className="bg-slate-50 min-h-screen">
            <Nav menuOpen={menuOpen} toggleMenu={toggleMenu} />
            
            <div className="max-w-4xl mx-auto px-4 pt-32 pb-16">
                <div className="text-center mb-12">
                    {/* Main Title - Increased weight and changed to nearly black (Slate-950) */}
                    <h1 className="text-5xl font-black text-slate-950 tracking-tight mb-2" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        Voting Guidelines
                    </h1>
                    {/* Subtitle - Darkened for better readability */}
                    <p className="text-xl text-slate-800 font-bold">
                        Cagayan de Oro National High School - Junior High
                    </p>
                    <div className="mt-4 inline-block bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-black shadow-md">
                        Election Date: February 11, 2026
                    </div>
                </div>

                <div className="space-y-8">
                    {/* Eligibility Section */}
                    <section className="bg-white rounded-2xl p-8 shadow-md border border-slate-200">
                        <div className="flex items-center gap-3 mb-6 text-blue-700">
                            <FiUserCheck className="w-8 h-8" />
                            <h2 className="text-3xl font-black text-slate-900">I. Eligibility & Requirements</h2>
                        </div>
                        <ul className="space-y-4 text-slate-700 text-lg">
                            <li className="flex items-start gap-3">
                                <span className="text-blue-600 font-black">▶</span>
                                <span><strong>Qualified Voters:</strong> Only current <strong>Grade 7, Grade 8, and Grade 9</strong> students are eligible.</span>
                            </li>
                            <li className="flex items-start gap-3 bg-red-50 p-4 rounded-xl border border-red-100">
                                <FiAlertCircle className="w-6 h-6 text-red-600 mt-1" />
                                <span className="text-red-900 font-bold"><strong>Note:</strong> Current Grade 10 students are NOT included.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-blue-600 font-black">▶</span>
                                <span><strong>Verification:</strong> Present School ID or verify with the Room Electoral Board.</span>
                            </li>
                        </ul>
                    </section>

                    {/* Process Section */}
                    <section className="bg-white rounded-2xl p-8 shadow-md border border-slate-200">
                        <div className="flex items-center gap-3 mb-6 text-green-700">
                            <FiEdit3 className="w-8 h-8" />
                            <h2 className="text-3xl font-black text-slate-900">II. Manual Ballot Process</h2>
                        </div>
                        <div className="grid gap-6 md:grid-cols-3">
                            <div className="p-5 bg-green-50 rounded-xl border border-green-100">
                                <span className="text-4xl font-black text-green-200 block mb-2">01</span>
                                <p className="text-base font-bold text-slate-800">Enter LRN, Full Name, and Grade/Section.</p>
                            </div>
                            <div className="p-5 bg-green-50 rounded-xl border border-green-100">
                                <span className="text-4xl font-black text-green-200 block mb-2">02</span>
                                <p className="text-base font-bold text-slate-800">Mark chosen candidates. Do not over-vote.</p>
                            </div>
                            <div className="p-5 bg-green-50 rounded-xl border border-green-100">
                                <span className="text-4xl font-black text-green-200 block mb-2">03</span>
                                <p className="text-base font-bold text-slate-800">Data will be encoded into the ERS System.</p>
                            </div>
                        </div>
                    </section>

                    {/* Selection Rules - High Visibility Table */}
                    <section className="bg-white rounded-2xl p-8 shadow-md border border-slate-200">
                        <div className="flex items-center gap-3 mb-6 text-purple-700">
                            <FiInfo className="w-8 h-8" />
                            <h2 className="text-3xl font-black text-slate-900">III. Selection Rules</h2>
                        </div>
                        
                        <div className="overflow-hidden rounded-xl border-2 border-slate-200 mb-8">
                            <table className="w-full text-left">
                                <thead className="bg-slate-900 text-white">
                                    <tr>
                                        <th className="px-6 py-4 text-lg font-black">Position</th>
                                        <th className="px-6 py-4 text-lg font-black text-center">Number to Select</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y-2 divide-slate-100">
                                    {["President", "Vice President", "Secretary", "Treasurer", "Auditor", "PIO (Internal)", "PIO (External)", "Protocol Officer"].map((pos) => (
                                        <tr key={pos} className="hover:bg-slate-50">
                                            <td className="px-6 py-4 text-slate-800 font-bold text-lg">{pos}</td>
                                            <td className="px-6 py-4 text-slate-800 text-center font-bold text-lg">1 Candidate</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-blue-600 text-white">
                                        <td className="px-6 py-4 text-xl font-black">Grade Level Representatives</td>
                                        <td className="px-6 py-4 text-center text-xl font-black">4 Candidates</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Crucial Logic Box */}
                        <div className="bg-slate-900 rounded-2xl p-8 text-white shadow-xl border-t-4 border-blue-500">
                            <h3 className="text-2xl font-black mb-6 flex items-center gap-3">
                                <FiAlertCircle className="text-blue-400" /> IMPORTANT VOTING RULE
                            </h3>
                            <div className="space-y-4 text-lg font-bold">
                                <div className="flex justify-between items-center bg-white/10 p-4 rounded-lg">
                                    <span className="text-blue-300">Grade 7 Voters</span>
                                    <span className="text-white">Vote for Grade 8 Reps</span>
                                </div>
                                <div className="flex justify-between items-center bg-white/10 p-4 rounded-lg">
                                    <span className="text-blue-300">Grade 8 Voters</span>
                                    <span className="text-white">Vote for Grade 9 Reps</span>
                                </div>
                                <div className="flex justify-between items-center bg-white/10 p-4 rounded-lg">
                                    <span className="text-blue-300">Grade 9 Voters</span>
                                    <span className="text-white">Vote for Grade 10 Reps</span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <footer className="mt-16 text-center text-slate-600 font-black text-lg tracking-widest">
                    — NOTHING FOLLOWS —
                </footer>
            </div>
        </div>
    );
};

export default Guidelines;